// LandingActZone — the "Two Ways to Act" cards + a live preview carousel
// of recent published acts. Shared between Home (`/`) and the Random Acts
// page (`/random-acts`) so the visitor sees the same "alive movement"
// section on both surfaces.
//
// Anchor id="act-now" — Hero's primary CTA smooth-scrolls here.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { User as UserIcon, Users as UsersIcon, ArrowRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingActZone({
  // Optional individual-card behaviour: pass a handler when the host page
  // already exposes its own act-submission form below (e.g. RandomActs).
  // When omitted (Home) the card routes to /random-acts.
  onIndividualClick,
  // Same idea for the community-event card — RandomActs now renders the
  // Community form inline too (2026-07-14 · Tiger). Home leaves it undefined
  // and the card <Link>s to /random-acts?path=community.
  onCommunityClick,
  individualHref = "/random-acts?path=individual#rac-form",
  communityHref = "/random-acts?path=community#rac-form",
  testidPrefix = "act-zone",
} = {}) {
  return (
    <>
      <section
        id="act-now"
        data-testid={`${testidPrefix}-cards`}
        className="relative pt-16 md:pt-20 pb-8 md:pb-10 bg-docm-cream scroll-mt-20"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="label-eyebrow text-docm-terracotta">Two ways to act</span>
            <h2 className="mt-3 h-display text-3xl md:text-5xl text-docm-ink leading-[1.05]">
              Bring a <span className="font-display italic text-docm-terracotta">conscious act</span> to life.
            </h2>
            <p className="mt-4 text-[15px] md:text-base text-docm-inkSoft leading-relaxed">
              One question. Two doors. Both add to the Wall of Consciousness.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {onIndividualClick ? (
              <button
                type="button"
                data-testid={`${testidPrefix}-individual`}
                onClick={onIndividualClick}
                className="group rounded-3xl bg-docm-paper border-2 border-docm-border hover:border-docm-terracotta hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(184,93,73,0.4)] transition-all duration-400 p-7 md:p-9 text-left"
              >
                <IndividualCardBody />
              </button>
            ) : (
              <Link
                to={individualHref}
                data-testid={`${testidPrefix}-individual`}
                className="group rounded-3xl bg-docm-paper border-2 border-docm-border hover:border-docm-terracotta hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(184,93,73,0.4)] transition-all duration-400 p-7 md:p-9 text-left block"
              >
                <IndividualCardBody />
              </Link>
            )}

            {onCommunityClick ? (
              <button
                type="button"
                data-testid={`${testidPrefix}-community`}
                onClick={onCommunityClick}
                className="group rounded-3xl bg-docm-paper border-2 border-docm-border hover:border-docm-moss hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(45,74,62,0.4)] transition-all duration-400 p-7 md:p-9 text-left"
              >
                <CommunityCardBody />
              </button>
            ) : (
              <Link
                to={communityHref}
                data-testid={`${testidPrefix}-community`}
                className="group rounded-3xl bg-docm-paper border-2 border-docm-border hover:border-docm-moss hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(45,74,62,0.4)] transition-all duration-400 p-7 md:p-9 text-left block"
              >
                <CommunityCardBody />
              </Link>
            )}
          </div>
        </div>
      </section>

      <RacPreviewCarousel testidPrefix={testidPrefix} />
    </>
  );
}

function CommunityCardBody() {
  return (
    <>
      <div className="flex items-center gap-4 mb-5">
        <div className="w-14 h-14 rounded-2xl bg-docm-moss/10 text-docm-moss flex items-center justify-center shrink-0">
          <UsersIcon size={26} />
        </div>
        <h3 className="font-serif-h text-xl md:text-2xl text-docm-ink leading-tight">
          Create a Consciousness Event
        </h3>
      </div>
      <p className="text-[14px] text-docm-inkSoft leading-relaxed">
        For people, communities, schools or organisations who want to organise a collective act of consciousness.
      </p>
      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[13.5px] text-docm-ink/85">
        {["Host a Consciousness Circle in your city", "Run an 11·11 event at your school or workplace", "Mobilise your community for a cause", "Convene an interfaith or intergenerational dialogue", "Launch a conscious drive at your organisation", "Stage a public conscious action"].map((t) => (
          <li key={t} className="flex items-start gap-1.5">
            <span className="text-docm-moss mt-0.5">✦</span>{t}
          </li>
        ))}
      </ul>
      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-docm-moss text-docm-cream font-sans font-semibold px-5 py-2.5 text-[13px] tracking-wide group-hover:bg-docm-mossHover transition-colors">
        Create a Consciousness Event
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </div>
    </>
  );
}

function IndividualCardBody() {
  return (
    <>
      <div className="flex items-center gap-4 mb-5">
        <div className="w-14 h-14 rounded-2xl bg-docm-terracotta/10 text-docm-terracotta flex items-center justify-center shrink-0">
          <UserIcon size={26} />
        </div>
        <h3 className="font-serif-h text-xl md:text-2xl text-docm-ink leading-tight">
          Perform a Random Act of Consciousness
        </h3>
      </div>
      <p className="text-[14px] text-docm-inkSoft leading-relaxed">
        For individuals who want to perform one conscious action and share it with the world.
      </p>
      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[13.5px] text-docm-ink/85">
        {["Champion truth in a hard conversation", "Defend a stranger's dignity", "Refuse to stay silent about injustice", "Sit with someone who is grieving", "Own a wrong you'd rather deny", "Turn one belief into daily practice"].map((t) => (
          <li key={t} className="flex items-start gap-1.5">
            <span className="text-docm-terracotta mt-0.5">✦</span>{t}
          </li>
        ))}
      </ul>
      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-docm-terracotta text-docm-cream font-sans font-semibold px-5 py-2.5 text-[13px] tracking-wide group-hover:bg-docm-terracotta/90 transition-colors">
        Perform a Random Act
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </div>
    </>
  );
}

// Live carousel preview — auto-scrolls 24 most-recently-approved acts.
function RacPreviewCarousel({ testidPrefix }) {
  const [acts, setActs] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/random-acts/public?limit=24`)
      .then((r) => { if (!cancelled) setActs(Array.isArray(r.data) ? r.data.filter((a) => a && (a.act || a.short)) : []); })
      .catch(() => { /* leave empty */ });
    return () => { cancelled = true; };
  }, []);

  if (acts.length === 0) return null;
  const loop = [...acts, ...acts];

  return (
    <section
      data-testid={`${testidPrefix}-carousel`}
      className="relative pt-8 md:pt-10 pb-14 md:pb-18 bg-docm-cream overflow-hidden"
    >
      <div className="max-w-[1300px] mx-auto px-5 md:px-10 mb-7">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="label-eyebrow text-docm-moss">Live · The Wall is Alive</span>
            <h2 className="mt-2 h-display text-2xl md:text-4xl text-docm-ink leading-tight">
              What people are doing <span className="font-display italic text-docm-moss">right now.</span>
            </h2>
          </div>
          <Link
            to="/random-acts#wall"
            data-testid={`${testidPrefix}-see-full-wall`}
            className="inline-flex items-center gap-1.5 text-[12.5px] uppercase tracking-[0.16em] font-semibold text-docm-moss hover:text-docm-mossHover group"
          >
            See the Complete Consciousness Wall
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>

      <div className="flex gap-4 md:gap-5 animate-marquee will-change-transform">
        {loop.map((a, i) => (
          <Link
            key={`${a.id}-${i}`}
            to="/random-acts#wall"
            data-testid={`${testidPrefix}-carousel-card-${i}`}
            className="shrink-0 w-[280px] md:w-[320px] p-5 md:p-6 rounded-2xl bg-docm-paper border border-docm-border hover:border-docm-moss/50 hover:shadow-[0_12px_30px_-15px_rgba(28,32,30,0.18)] transition-all duration-300"
          >
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-docm-moss font-semibold">
              <Sparkles size={10} />
              {a.country || "Global"}
            </div>
            <p className="mt-3 text-[14px] text-docm-ink leading-[1.55] line-clamp-4">
              &ldquo;{a.act || a.short || ""}&rdquo;
            </p>
            <div className="mt-4 text-[12px] text-docm-inkSoft">
              — {a.name || "Anonymous"}
            </div>
          </Link>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-32 bg-gradient-to-r from-docm-cream/95 to-transparent z-10" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-32 bg-gradient-to-l from-docm-cream/95 to-transparent z-10" aria-hidden="true" />
    </section>
  );
}
